import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

export interface FactoryInfo {
  id: string
  name: string
  address: string
  city: string
  country: string
  phone?: string
  email: string
  fax?: string
  working_hours?: string
  google_maps_url?: string
  is_primary: boolean
  created_at: string
  updated_at: string
}

export type FactoryInfoInput = Omit<FactoryInfo, 'id' | 'created_at' | 'updated_at'>

// Fetch all factory locations
export function useFactoryInfoList() {
  return useQuery({
    queryKey: ['factory-info'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('factory_info')
        .select('*')
        .order('is_primary', { ascending: false })
        .order('name')

      if (error) throw error
      return (data || []) as FactoryInfo[]
    },
  })
}

// Fetch primary factory info (for public display)
export function usePrimaryFactoryInfo() {
  return useQuery({
    queryKey: ['factory-info', 'primary'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('factory_info')
        .select('*')
        .eq('is_primary', true)
        .single()

      if (error && error.code !== 'PGRST116') throw error // PGRST116 = no rows found
      return data as FactoryInfo | null
    },
  })
}

// Create factory info
export function useCreateFactoryInfo() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: FactoryInfoInput) => {
      // If this is set as primary, unset other primaries first
      if (input.is_primary) {
        await supabase
          .from('factory_info')
          .update({ is_primary: false })
          .eq('is_primary', true)
      }

      const { data, error } = await supabase
        .from('factory_info')
        .insert(input)
        .select()
        .single()

      if (error) throw error
      return data as FactoryInfo
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['factory-info'] })
    },
  })
}

// Update factory info
export function useUpdateFactoryInfo() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...input }: Partial<FactoryInfoInput> & { id: string }) => {
      // If this is set as primary, unset other primaries first
      if (input.is_primary) {
        await supabase
          .from('factory_info')
          .update({ is_primary: false })
          .neq('id', id)
      }

      const { data, error } = await supabase
        .from('factory_info')
        .update({ ...input, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data as FactoryInfo
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['factory-info'] })
    },
  })
}

// Delete factory info
export function useDeleteFactoryInfo() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('factory_info')
        .delete()
        .eq('id', id)

      if (error) throw error
      return id
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['factory-info'] })
    },
  })
}
